import { BaseComponent, BElement } from "utils/base-component"

/**
 * Configuration interface for carousel components.
 *
 * @interface CarouselConfig
 * @property {string} carouselSelector - CSS selector for the main carousel container element
 * @property {string} slideSelector - CSS selector for individual slide elements within the carousel
 * @property {number} slideGap - Gap between slides in pixels (used for snap calculations)
 * @property {string} cssClass - CSS class name for the carousel component (used for styling)
 */
export interface CarouselConfig {
  carouselSelector: string
  slideSelector: string
  slideGap: number
  cssClass: string
}

/**
 * Base carousel class that provides drag-to-scroll functionality with smooth snapping.
 *
 * Features:
 * - Mouse drag to scroll through slides
 * - Smooth snapping to slide positions
 * - Prevents link clicks during drag operations
 * - Cursor feedback (grab/grabbing)
 * - Configurable slide gaps and selectors
 *
 * @class Carousel
 * @extends BaseComponent<CarouselConfig>
 */
export class Carousel extends BaseComponent<CarouselConfig> {
  private carousel!: BElement
  private isDragging = false
  private startX = 0
  private scrollLeft = 0
  private draggingTimeout: number | null = null

  /**
   * Creates a new carousel instance with the provided configuration.
   *
   * @param {CarouselConfig} config - Configuration object containing selectors and settings
   */
  constructor(config: CarouselConfig) {
    super(null, config)
  }

  /**
   * Initializes the carousel by setting up event listeners and cursor styles.
   * Called automatically when the component is created.
   */
  init() {
    this.carousel = new BElement(
      document.querySelector(this.config.carouselSelector) as Element,
      this.cssClass as string,
    )

    if (this.carousel && this.carousel.element) {
      // Add mouse event listeners for drag functionality
      this.carousel.element.addEventListener("mousedown", this.startDrag.bind(this))
      this.carousel.element.addEventListener("mouseleave", this.endDrag.bind(this))
      this.carousel.element.addEventListener("mouseup", this.endDrag.bind(this))
      this.carousel.element.addEventListener("mousemove", this.drag.bind(this))

      // Global mouse up listener to catch releases outside carousel
      document.addEventListener("mouseup", this.endDrag.bind(this))

      // Prevent link clicks when dragging
      this.preventLinkClicksDuringDrag()

      // Add cursor styles to indicate draggable content
      this.carousel.element.style.cursor = "grab"
    }
  }

  private preventLinkClicksDuringDrag() {
    if (!this.carousel.element) return

    // Find all links within the carousel
    const links = this.carousel.element.querySelectorAll("a")

    links.forEach((link) => {
      link.addEventListener("click", (e: Event) => {
        if (this.isDragging) {
          // console.log('Preventing link click during drag')
          e.preventDefault()
          e.stopPropagation()
        }
      })
    })
  }

  private startDrag(e: MouseEvent) {
    if (!this.carousel.element) return

    // Get the clicked element and check if it's a scroll button or within its area
    const clickX = e.clientX
    const carouselRect = this.carousel.element.getBoundingClientRect()
    const buttonWidth = 40 // Approximate width of the scroll button area

    // Check if click is in left or right button area
    if (clickX - carouselRect.left < buttonWidth || carouselRect.right - clickX < buttonWidth) {
      return // Click is in button area, don't initiate drag
    }

    if (this.draggingTimeout) {
      window.clearTimeout(this.draggingTimeout)
      this.draggingTimeout = null
    }

    this.isDragging = true
    // console.log('Drag started, isDragging:', this.isDragging)
    this.startX = e.pageX - this.carousel.element.offsetLeft
    this.scrollLeft = this.carousel.element.scrollLeft

    // Wait to add dragging class until mouse moves
    this.carousel.element.style.cursor = "grabbing"
    e.preventDefault()
  }

  private endDrag() {
    if (!this.carousel.element || !this.isDragging) return

    this.carousel.element.style.cursor = "grab"

    // Only calculate snap position if we were actually dragging
    if (this.carousel.element.classList.contains("dragging")) {
      // Calculate target position for smooth snapping
      this.calculateSnapPosition()

      // Remove dragging class after delay for smooth transition
      this.draggingTimeout = window.setTimeout(() => {
        if (this.carousel.element) {
          this.carousel.element.classList.remove("dragging")
        }
        this.draggingTimeout = null

        // Reset drag state after delay
        setTimeout(() => {
          this.isDragging = false
          // console.log('Drag state reset, isDragging:', this.isDragging)
        }, 100)
      }, 50) // Short delay before re-enabling snap
    } else {
      // No drag occurred, reset immediately
      this.isDragging = false
      // console.log('Click detected, reset immediately')
    }

    // Safety cleanup after longer timeout
    setTimeout(() => {
      if (this.carousel.element && this.carousel.element.classList.contains("dragging")) {
        // console.log('Safety cleanup: removing stuck dragging class')
        this.carousel.element.classList.remove("dragging")
        this.isDragging = false
      }
    }, 500)
  }

  private calculateSnapPosition() {
    if (!this.carousel.element) return

    const currentScroll = this.carousel.element.scrollLeft

    // Get slide width (including gap)
    const slides = this.carousel.element.querySelectorAll(this.config.slideSelector)
    if (!slides.length) return

    const slideRect = slides[0].getBoundingClientRect()
    const slideWidth = slideRect.width + this.config.slideGap

    // Calculate which slide we should snap to
    const slideIndex = Math.round(currentScroll / slideWidth)
    const targetPosition = slideIndex * slideWidth

    // Add smooth scrolling class
    this.carousel.element.classList.add("smooth-return")

    // Scroll to the target position
    this.carousel.element.scrollLeft = targetPosition

    // Remove smooth class after transition completes
    window.setTimeout(() => {
      if (this.carousel.element) {
        this.carousel.element.classList.remove("smooth-return")
      }
    }, 300) // Match this with the transition duration in CSS
  }

  private drag(e: MouseEvent) {
    if (!this.isDragging || !this.carousel.element) return

    // Add dragging class on first move
    if (!this.carousel.element.classList.contains("dragging")) {
      this.carousel.element.classList.add("dragging")
      // console.log('Added dragging class - links disabled')
    }

    const x = e.pageX - this.carousel.element.offsetLeft
    const walk = (x - this.startX) * 1.5 // Scroll multiplier - adjust for faster/slower drag
    this.carousel.element.scrollLeft = this.scrollLeft - walk

    // Prevent default behaviors while dragging
    e.preventDefault()
  }

  /**
   * Public method to force cleanup of drag state.
   * Useful for manually resetting the carousel if needed.
   */
  public cleanupDragState() {
    if (this.carousel.element) {
      this.carousel.element.classList.remove("dragging")
      this.carousel.element.style.cursor = "grab"
      this.isDragging = false
      if (this.draggingTimeout) {
        window.clearTimeout(this.draggingTimeout)
        this.draggingTimeout = null
      }
      // console.log('Forced cleanup of drag state')
    }
  }
}

/**
 * Factory function for creating carousel instances.
 *
 * @param {CarouselConfig} config - Configuration object for the carousel
 * @returns {Carousel} A new carousel instance
 */
export function createCarousel(config: CarouselConfig): Carousel {
  return new Carousel(config)
}

/**
 * HOW TO CREATE A NEW CAROUSEL COMPONENT
 * ======================================
 *
 * 1. Create a new component directory:
 *    frontend/components/carousel-my-component/
 *
 * 2. Create the component file (index.ts):
 *    ```typescript
 *    import { createCarousel, CarouselConfig } from 'utils/carousel'
 *    import './index.scss'
 *
 *    require.context('./static', true, /\.jpg|svg$/)
 *
 *    // Component-specific configuration
 *    const config: CarouselConfig = {
 *      carouselSelector: '.carousel-my-component__carousel',
 *      slideSelector: '.carousel-my-component__carousel__slide',
 *      slideGap: 20, // Gap between slides in pixels
 *      cssClass: 'carousel-my-component'
 *    }
 *
 *    // Export the configured carousel instance
 *    export const CarouselMyComponent = createCarousel(config)
 *    ```
 *
 * 3. Create your HTML structure with the matching selectors:
 *    ```html
 *    <div class="carousel-my-component">
 *      <div class="carousel-my-component__carousel">
 *        <div class="carousel-my-component__carousel__slide">Slide 1</div>
 *        <div class="carousel-my-component__carousel__slide">Slide 2</div>
 *        <div class="carousel-my-component__carousel__slide">Slide 3</div>
 *      </div>
 *    </div>
 *    ```
 *
 * 4. Add your CSS styles in index.scss:
 *    ```scss
 *    .carousel-my-component {
 *      &__carousel {
 *        display: flex;
 *        overflow-x: auto;
 *        scroll-snap-type: x mandatory;
 *        gap: 1.25rem; // Should match slideGap in config
 *
 *        &__slide {
 *          flex: 0 0 auto;
 *          scroll-snap-align: start;
 *        }
 *      }
 *    }
 *    ```
 *
 * 5. Import and use in your application:
 *    ```typescript
 *    import { CarouselMyComponent } from 'components/carousel-my-component'
 *
 *    // The carousel will automatically initialize when the component is imported
 *    ```
 *
 * CONFIGURATION OPTIONS:
 * =====================
 *
 * - carouselSelector: CSS selector for the scrollable container
 * - slideSelector: CSS selector for individual slides
 * - slideGap: Gap between slides in pixels (used for snap calculations)
 * - cssClass: CSS class name for the component (used for styling)
 *
 * FEATURES INCLUDED:
 * =================
 *
 * - Mouse drag to scroll through slides
 * - Smooth snapping to slide positions
 * - Prevents link clicks during drag operations
 * - Cursor feedback (grab/grabbing)
 * - Automatic cleanup of drag state
 * - Responsive to different slide sizes
 *
 * EXAMPLES:
 * =========
 *
 * See existing components for reference:
 * - frontend/components/carousel-nearby-mountains/
 * - frontend/components/carousel-photo-gallery/
 * - frontend/components/carousel-news-posts/
 * - frontend/components/carousel-popular-mountains/
 */
